// 20f — the testable rules behind the Integrations grid.
//
// The screen's argument, taken straight from the plan: a green badge with no
// timestamp is how a broken integration hides for a fortnight. So every card
// says when the connector last *succeeded*, not merely that it is connected.
//
// That turns out to be answerable. `integration.lastSyncAt` is written by the
// hub ONLY on a successful probe — the failure branch passes `undefined`, which
// Prisma reads as "leave it alone" — so the column already holds the last
// success and nothing else. It is labelled accordingly here rather than as
// "last sync", which would read as "last attempt" and mean the opposite.
//
// The second rule is duller and just as load-bearing: the status vocabularies
// do not match. Prisma stores ACTIVE / INACTIVE / ERROR / PENDING_AUTH; the
// client enum declares 'connected' / 'disconnected' / 'error' / 'pending'. A
// comparison against the client enum therefore never matches, and every
// connector would render as an unrecognised status. Both vocabularies are
// normalised in one place, here, where a test can hold them.

import type {
  Integration,
  IntegrationType,
  Webhook,
  WebhookDelivery,
} from '@services/integration.service';

export type Health = 'working' | 'failing' | 'attention' | 'idle' | 'unconfigured';

export const HEALTH_TEXT: Record<Health, string> = {
  working: 'Connected',
  failing: 'Failing',
  attention: 'Needs attention',
  idle: 'Switched off',
  unconfigured: 'Not connected',
};

/**
 * The server's status, whichever vocabulary it arrived in.
 *
 * Case is normalised too: Prisma enums serialise uppercase, the older client
 * enum is lowercase, and a tenant may hold rows written by either.
 */
export function normaliseStatus(raw: string | null | undefined): Health {
  switch ((raw ?? '').trim().toUpperCase()) {
    case 'ACTIVE':
    case 'CONNECTED':
      return 'working';
    case 'ERROR':
    case 'FAILED':
      return 'failing';
    case 'PENDING_AUTH':
    case 'PENDING':
      return 'attention';
    case 'INACTIVE':
    case 'DISCONNECTED':
      return 'idle';
    default:
      return 'unconfigured';
  }
}

export interface ConnectorCard {
  id: string;
  name: string;
  type: IntegrationType;
  health: Health;
  /** When it last succeeded, or null when it never has. */
  lastSuccessAt: string | null;
  /** What the hub recorded the last time it failed, when it did. */
  error: { message: string; at: string | null } | null;
  enabled: boolean;
}

const text = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

/**
 * A connector as the grid renders it.
 *
 * A row that is switched off but healthy reads as 'idle', not 'working': an
 * integration nobody disabled and an integration somebody disabled are
 * different facts, and only the first is a problem.
 */
export function connectorCard(integration: Integration): ConnectorCard {
  const status = normaliseStatus(integration.status);
  const log = integration.errorLog;
  const enabled = integration.isEnabled !== false;

  return {
    id: integration.id,
    name: integration.name,
    type: integration.type,
    health: status === 'working' && !enabled ? 'idle' : status,
    lastSuccessAt: text(integration.lastSyncAt),
    error:
      status === 'failing' && log
        ? { message: text(log.error) ?? 'No reason recorded', at: text(log.timestamp) }
        : null,
    enabled,
  };
}

export type Cohort = 'all' | 'working' | 'attention' | 'off';

export const COHORT_TEXT: Record<Cohort, string> = {
  all: 'All',
  working: 'Connected',
  attention: 'Needs attention',
  off: 'Not connected',
};

export function inCohort(card: ConnectorCard, cohort: Cohort): boolean {
  switch (cohort) {
    case 'working':
      return card.health === 'working';
    case 'attention':
      return card.health === 'failing' || card.health === 'attention';
    case 'off':
      return card.health === 'idle' || card.health === 'unconfigured';
    default:
      return true;
  }
}

export function cohortCounts(cards: ConnectorCard[]): Record<Cohort, number> {
  return {
    all: cards.length,
    working: cards.filter((c) => inCohort(c, 'working')).length,
    attention: cards.filter((c) => inCohort(c, 'attention')).length,
    off: cards.filter((c) => inCohort(c, 'off')).length,
  };
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "4 min ago" · "2 h ago" · "11 Sep".
 *
 * Returns null — never "just now", never "—" — when there is no timestamp, so
 * the caller has to decide what an absent success means. On a connector card it
 * means "never", which is the single most important thing the card can say.
 */
export function ago(iso: string | null, now = Date.now()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const delta = now - t;
  if (delta < MINUTE) return 'less than a minute ago';
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)} min ago`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)} h ago`;
  if (delta < 7 * DAY) return `${Math.floor(delta / DAY)} d ago`;
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * The line under the badge: when this connector last actually worked.
 *
 * A connector that has never succeeded says so outright. One that is failing
 * says how long it has been since the last success, because "failing" alone
 * does not distinguish a blip from a fortnight of silence.
 */
export function lastSuccessText(card: ConnectorCard, now = Date.now()): string {
  const when = ago(card.lastSuccessAt, now);
  if (when) return `Last succeeded ${when}`;
  return card.health === 'unconfigured'
    ? 'Never connected'
    : 'Never succeeded — nothing has got through yet';
}

export interface WebhookHealth {
  endpoints: number;
  enabled: number;
  /** Endpoints whose most recent delivery failed. */
  failing: number;
  /** Deliveries queued for another attempt. */
  retrying: number;
  /** Endpoints whose deliveries were not read, so the counts above exclude them. */
  unchecked: number;
}

/**
 * What the webhook endpoints are actually doing.
 *
 * `failing` counts ENDPOINTS by their most recent delivery, not deliveries: a
 * webhook that failed forty times last month and succeeded this morning is
 * working, and counting raw failures would keep it red forever. `unchecked`
 * exists so a page that only sampled some endpoints cannot imply it read them
 * all.
 */
export function webhookHealth(
  webhooks: Webhook[],
  deliveries: Map<string, WebhookDelivery[] | undefined>,
): WebhookHealth {
  let failing = 0;
  let retrying = 0;
  let unchecked = 0;

  for (const hook of webhooks) {
    const log = deliveries.get(hook.id);
    if (!log) {
      unchecked += 1;
      continue;
    }
    const sorted = [...log].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    const latest = sorted[0];
    if (latest && String(latest.status).toUpperCase() === 'FAILED') failing += 1;
    retrying += log.filter((d) => d.nextRetryAt && String(d.status).toUpperCase() !== 'SUCCESS')
      .length;
  }

  return {
    endpoints: webhooks.length,
    enabled: webhooks.filter((w) => w.isEnabled).length,
    failing,
    retrying,
    unchecked,
  };
}
